<template>
  <div class="metrics">
    <!-- Header toolbar -->
    <div class="toolbar">
      <LgButton class="only-mobile" size="sm" variant="ghost" @click="catalogSheet = true">
        <template #icon><ChartSpline :size="15" aria-hidden="true" /></template>
        Metrics
      </LgButton>

      <div class="toolbar-spacer" />

      <ExplorationsMenu :spec="currentSpec" @apply="onApplyExploration" />

      <LgTooltip text="Dashboards are coming soon" placement="bottom">
        <LgButton size="sm" variant="ghost" disabled>
          <template #icon><LayoutDashboard :size="14" aria-hidden="true" /></template>
          Add to dashboard
        </LgButton>
      </LgTooltip>

      <TimeRangePicker :model-value="range" @update:model-value="setRange" />

      <LgTooltip :text="liveTitle" placement="bottom">
        <LgButton
          class="live-toggle"
          :class="live ? 'live' : 'off'"
          size="sm"
          variant="ghost"
          :aria-pressed="live"
          @click="live = !live"
        >
          <template #icon>
            <Play v-if="live" :size="14" aria-hidden="true" />
            <Pause v-else :size="14" aria-hidden="true" />
          </template>
          {{ live ? 'Live' : 'Paused' }}
        </LgButton>
      </LgTooltip>
    </div>

    <div class="body">
      <!-- Catalog (desktop panel) -->
      <aside class="catalog-panel only-desktop">
        <MetricCatalog
          :metrics="catalog"
          :loading="catalogLoading"
          :error="catalogError"
          :search="search"
          :selected-key="selectedKey"
          @update:search="search = $event"
          @pick="onPickMetric"
        />
      </aside>

      <!-- Chart canvas + controls -->
      <main class="canvas">
        <template v-if="builder">
          <div class="builder-bar">
            <div class="chip-row">
              <LgChip tone="accent" removable remove-label="Remove metric" @remove="clearMetric">
                {{ humanize(builder.metric.name) }}
              </LgChip>

              <LgChip
                v-if="builder.splitBy"
                tone="info"
                removable
                remove-label="Remove split"
                @remove="
                  builder.splitBy = null;
                  runQuery();
                "
              >
                split: {{ labelDisplayName(builder.splitBy) }}
              </LgChip>

              <LgChip
                v-for="(f, i) in builder.filters"
                :key="`${f.label}${f.op}${f.value}`"
                tone="neutral"
                removable
                remove-label="Remove filter"
                @remove="removeFilter(i)"
              >
                {{ labelDisplayName(f.label) }} {{ f.op === 'neq' ? '≠' : '=' }} {{ f.value }}
              </LgChip>
            </div>

            <div class="control-row">
              <MetricLabelDropdown
                mode="split"
                :metric="builder.metric"
                :label-options="splitOptions"
                :from="windowFrom"
                :to="windowTo"
                @split="onSplit"
              />
              <MetricLabelDropdown
                mode="filter"
                :metric="builder.metric"
                :label-options="filterOptions"
                :from="windowFrom"
                :to="windowTo"
                @add="onAddFilter"
              />

              <VizTypePicker v-model="builder.viz" :options="vizOpts" />

              <LgButton
                size="sm"
                variant="ghost"
                :aria-pressed="optionsOpen"
                @click="optionsOpen = !optionsOpen"
              >
                <template #icon><SlidersHorizontal :size="14" aria-hidden="true" /></template>
                Options
              </LgButton>
            </div>

            <!-- Advanced options (progressive disclosure) -->
            <div v-if="optionsOpen" class="options-row">
              <label class="opt">
                <span class="opt-label">Aggregation</span>
                <LgSelect
                  :model-value="builder.agg"
                  :options="aggSelectOptions"
                  aria-label="Aggregation"
                  @update:model-value="onAggChange($event)"
                />
              </label>
              <label class="opt">
                <span class="opt-label">Unit</span>
                <LgSelect
                  :model-value="builder.yUnit"
                  :options="unitOptions"
                  aria-label="Y-axis unit"
                  @update:model-value="builder.yUnit = $event as MetricUnit"
                />
              </label>
              <label class="opt">
                <span class="opt-label">Scale</span>
                <LgSelect
                  :model-value="builder.yScale"
                  :options="scaleOptions"
                  aria-label="Y-axis scale"
                  @update:model-value="builder.yScale = $event as 'linear' | 'log'"
                />
              </label>
            </div>
          </div>

          <!-- Single-value viz (counter/gauge) -->
          <div v-if="isSingleValue" class="single-grid">
            <template v-if="visibleSeries.length">
              <template v-for="s in visibleSeries" :key="s.name">
                <LgStatTile
                  v-if="builder.viz === 'counter'"
                  :label="displayName(s.name)"
                  :value="lastValue(s) ?? 0"
                  :unit="builder.yUnit"
                  :spark="s.points.map((p) => p[1])"
                />
                <LgGauge
                  v-else
                  variant="bar"
                  :value="lastValue(s) ?? 0"
                  :label="displayName(s.name)"
                  :unit="builder.yUnit"
                  :max="gaugeMax(s)"
                />
              </template>
            </template>
            <div v-else class="canvas-empty">
              <GoblinIcon variant="empty" :size="48" />
              <p>{{ queryError || 'No data in this range.' }}</p>
            </div>
          </div>

          <!-- Time-series viz -->
          <LgChart
            v-else
            :series="chartSeries"
            :type="chartType"
            :y-unit="builder.yUnit"
            :y-scale="builder.yScale"
            :height="320"
            live
            :loading="queryLoading && !chartSeries.length"
            :empty-label="queryError || 'No data in this range'"
          />

          <p v-if="truncated" class="truncate-note">
            Showing a downsampled/limited slice — narrow the range or add a filter for full detail.
          </p>

          <!-- Legend -->
          <SeriesLegend
            v-if="!isSingleValue && legendRows.length"
            class="legend"
            :series="legendRows"
            :unit="builder.yUnit"
            :aliases="builder.aliases"
            :colors="builder.colors"
            :hidden="hiddenSeries"
            @isolate="isolated = $event"
            @toggle-hidden="onToggleHidden"
            @set-alias="onSetAlias"
            @set-color="onSetColor"
            @hover="() => {}"
          />
        </template>

        <!-- No metric picked yet -->
        <div v-else class="canvas-empty prompt">
          <GoblinIcon variant="mark" :size="64" />
          <p class="prompt-title">Pick a metric to chart</p>
          <p class="prompt-sub only-desktop">
            Browse the catalog on the left — each metric previews its recent shape before you
            commit.
          </p>
          <LgButton class="only-mobile" variant="primary" @click="catalogSheet = true">
            Browse metrics
          </LgButton>
        </div>
      </main>
    </div>

    <!-- Catalog bottom-sheet (mobile) -->
    <LgDrawer :open="catalogSheet" side="left" title="Metrics" @close="catalogSheet = false">
      <MetricCatalog
        :metrics="catalog"
        :loading="catalogLoading"
        :error="catalogError"
        :search="search"
        :selected-key="selectedKey"
        @update:search="search = $event"
        @pick="onPickMetricMobile"
      />
    </LgDrawer>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { ChartSpline, LayoutDashboard, Play, Pause, SlidersHorizontal } from 'lucide-vue-next';
import {
  api,
  ApiError,
  type CatalogMetric,
  type MetricMatcher,
  type MetricSeries,
} from '../api.js';
import { RANGE_MS, DEFAULT_RANGE, type TimeRange } from '../composables/useFilters.js';
import { type MetricUnit } from '../lib/format-metric.js';
import { resolveVizPalette, colorForName } from '../lib/viz-palette.js';
import {
  type BuilderState,
  type ExplorationSpec,
  builderFromMetric,
  builderFromSpec,
  specFromBuilder,
  buildRangeQuery,
  seriesName,
  labelDisplayName,
  splittableLabels,
  aggOptions,
  vizOptions,
} from '../lib/metric-exploration.js';
import type { ChartSeries, VizKind } from '../ui/LgChart.vue';
import type { LegendRow } from '../components/metrics/SeriesLegend.vue';
import MetricCatalog from '../components/metrics/MetricCatalog.vue';
import MetricLabelDropdown from '../components/metrics/MetricLabelDropdown.vue';
import SeriesLegend from '../components/metrics/SeriesLegend.vue';
import ExplorationsMenu from '../components/metrics/ExplorationsMenu.vue';
import TimeRangePicker from '../components/TimeRangePicker.vue';
import VizTypePicker from '../ui/VizTypePicker.vue';
import LgChart from '../ui/LgChart.vue';
import LgStatTile from '../ui/LgStatTile.vue';
import LgGauge from '../ui/LgGauge.vue';
import LgChip from '../ui/LgChip.vue';
import LgButton from '../ui/LgButton.vue';
import LgSelect from '../ui/LgSelect.vue';
import LgTooltip from '../ui/LgTooltip.vue';
import LgDrawer from '../ui/LgDrawer.vue';
import GoblinIcon from '../components/GoblinIcon.vue';

const emit = defineEmits<{ unauthorized: [] }>();

// Metrics refresh on the SCRAPE cadence (≥15s, §5) — NOT the 2s log cadence.
const POLL_MS = 15_000;

const range = ref<TimeRange>(DEFAULT_RANGE);
const live = ref(true);
const liveTitle = computed(() =>
  live.value
    ? `Auto-refresh every ${POLL_MS / 1000}s (scrape-aligned). Click to pause.`
    : 'Auto-refresh is off. Click to resume.',
);

// --- Catalog --------------------------------------------------------------
const catalog = ref<CatalogMetric[]>([]);
const catalogLoading = ref(false);
const catalogError = ref(false);
const search = ref('');
const catalogSheet = ref(false);

// --- Builder --------------------------------------------------------------
const builder = ref<BuilderState | null>(null);
const optionsOpen = ref(false);
const selectedKey = computed(() =>
  builder.value
    ? `${builder.value.metric.source} ${builder.value.metric.target} ${builder.value.metric.name}`
    : null,
);
const currentSpec = computed<ExplorationSpec | null>(() =>
  builder.value ? specFromBuilder(builder.value) : null,
);

// --- Query results --------------------------------------------------------
const querySeries = ref<MetricSeries[]>([]);
const truncated = ref(false);
const queryLoading = ref(false);
const queryError = ref('');
const hiddenSeries = ref<string[]>([]);
const isolated = ref<string[]>([]);
let querySeq = 0;

// --- Window ---------------------------------------------------------------
const windowTo = ref(Date.now());
const windowFrom = computed(() => windowTo.value - RANGE_MS[range.value]);

// --- Options ---------------------------------------------------------------
const vizOpts = vizOptions();
const scaleOptions = [
  { value: 'linear', label: 'Linear' },
  { value: 'log', label: 'Logarithmic' },
];
const unitOptions = [
  { value: 'none', label: 'Auto / number' },
  { value: 'bytes', label: 'Bytes' },
  { value: 'percent', label: 'Percent' },
  { value: 'ms', label: 'Duration (ms)' },
  { value: 'count', label: 'Count' },
  { value: 'ratio', label: 'Ratio' },
];
const aggSelectOptions = computed(() =>
  builder.value ? aggOptions(builder.value.metric.type).map((a) => ({ value: a, label: a })) : [],
);
const splitOptions = computed(() => (builder.value ? splittableLabels(builder.value.metric) : []));
const filterOptions = splitOptions; // same dimension set

// --- Rendering ------------------------------------------------------------
const palette = ref<string[]>([]);
function colorFor(name: string): string {
  if (builder.value?.colors[name]) return builder.value.colors[name];
  return colorForName(name, palette.value.length ? palette.value : ['#58a6ff']);
}
function displayName(name: string): string {
  return builder.value?.aliases[name] || name;
}

/** Result series named by their (non-`__`) label values, with the metric name as
 * fallback for the single unsplit series (spec §4.2.4). */
const namedSeries = computed(() => {
  const b = builder.value;
  if (!b) return [] as Array<{ name: string; points: [number, number][] }>;
  return querySeries.value.map((s) => ({
    name: seriesName(s.labels, b.metric.name, b.splitBy),
    points: s.points,
  }));
});

/** Series that pass the legend's hide + isolate filters. */
const visibleSeries = computed(() =>
  namedSeries.value.filter(
    (s) =>
      !hiddenSeries.value.includes(s.name) &&
      (isolated.value.length === 0 || isolated.value.includes(s.name)),
  ),
);

const chartType = computed<VizKind>(() => {
  const v = builder.value?.viz;
  return v === 'bar' || v === 'area' || v === 'points' ? v : 'line';
});
const chartSeries = computed<ChartSeries[]>(() =>
  visibleSeries.value.map((s) => ({
    name: displayName(s.name),
    points: s.points,
    color: colorFor(s.name),
  })),
);

const isSingleValue = computed(
  () => builder.value?.viz === 'counter' || builder.value?.viz === 'gauge',
);

const legendRows = computed<LegendRow[]>(() =>
  namedSeries.value.map((s) => {
    const vals = s.points.map((p) => p[1]).filter((v) => Number.isFinite(v));
    const min = vals.length ? Math.min(...vals) : null;
    const max = vals.length ? Math.max(...vals) : null;
    return {
      name: s.name,
      color: colorFor(s.name),
      current: vals.length ? vals[vals.length - 1] : null,
      min,
      max,
      variance: min != null && max != null ? max - min : 0,
    };
  }),
);

function lastValue(s: { points: [number, number][] }): number | null {
  for (let i = s.points.length - 1; i >= 0; i--) {
    if (Number.isFinite(s.points[i][1])) return s.points[i][1];
  }
  return null;
}
function gaugeMax(s: { points: [number, number][] }): number {
  if (builder.value?.yUnit === 'percent') return 100;
  const vals = s.points.map((p) => p[1]).filter((v) => Number.isFinite(v));
  return vals.length ? Math.max(...vals, 1) : 100;
}

const PROM_SUFFIXES = ['_bucket', '_count', '_sum', '_total'];
function humanize(name: string): string {
  let base = name;
  for (const suf of PROM_SUFFIXES) {
    if (base.endsWith(suf)) {
      base = base.slice(0, -suf.length);
      break;
    }
  }
  const words = base.replace(/[._]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// --- Data loading ---------------------------------------------------------
function onUnauthorized(e: unknown) {
  if (e instanceof ApiError && e.unauthorized) emit('unauthorized');
}

let catalogInFlight = false;
async function loadCatalog() {
  if (catalogInFlight) return;
  catalogInFlight = true;
  catalogLoading.value = catalog.value.length === 0;
  try {
    const now = Date.now();
    const res = await api.metrics.catalog({
      from: now - RANGE_MS[range.value],
      to: now,
      search: undefined,
    });
    catalog.value = res.metrics;
    catalogError.value = false;
  } catch (e) {
    catalogError.value = true;
    onUnauthorized(e);
  } finally {
    catalogLoading.value = false;
    catalogInFlight = false;
  }
}

async function runQuery() {
  const b = builder.value;
  if (!b) return;
  windowTo.value = Date.now();
  const body = buildRangeQuery(b, windowFrom.value, windowTo.value);
  const seq = ++querySeq;
  queryLoading.value = true;
  try {
    const res = await api.metrics.query(body);
    if (seq !== querySeq) return;
    querySeries.value = res.series;
    truncated.value = res.truncated;
    queryError.value = '';
  } catch (e) {
    if (seq !== querySeq) return;
    querySeries.value = [];
    queryError.value = e instanceof ApiError ? e.message : 'Query failed.';
    onUnauthorized(e);
  } finally {
    if (seq === querySeq) queryLoading.value = false;
  }
}

// --- Interactions ---------------------------------------------------------
function onPickMetric(m: CatalogMetric) {
  builder.value = builderFromMetric(m);
  resetSeriesView();
  runQuery();
}
function onPickMetricMobile(m: CatalogMetric) {
  onPickMetric(m);
  catalogSheet.value = false;
}
function clearMetric() {
  builder.value = null;
  querySeries.value = [];
  resetSeriesView();
}
function resetSeriesView() {
  hiddenSeries.value = [];
  isolated.value = [];
}

function onSplit(label: string) {
  if (!builder.value) return;
  builder.value.splitBy = label;
  resetSeriesView();
  runQuery();
}
function onAddFilter(m: MetricMatcher) {
  if (!builder.value) return;
  // De-dupe identical matchers.
  if (
    builder.value.filters.some((f) => f.label === m.label && f.op === m.op && f.value === m.value)
  )
    return;
  builder.value.filters.push(m);
  resetSeriesView();
  runQuery();
}
function removeFilter(i: number) {
  if (!builder.value) return;
  builder.value.filters.splice(i, 1);
  runQuery();
}
function onAggChange(agg: string) {
  if (!builder.value) return;
  builder.value.agg = agg as BuilderState['agg'];
  runQuery();
}

function onToggleHidden(name: string) {
  const set = new Set(hiddenSeries.value);
  set.has(name) ? set.delete(name) : set.add(name);
  hiddenSeries.value = [...set];
}
function onSetAlias(name: string, alias: string) {
  if (!builder.value) return;
  if (alias) builder.value.aliases[name] = alias;
  else delete builder.value.aliases[name];
}
function onSetColor(name: string, color: string) {
  if (!builder.value) return;
  builder.value.colors[name] = color;
}

function onApplyExploration(spec: ExplorationSpec) {
  builder.value = builderFromSpec(spec);
  resetSeriesView();
  runQuery();
}

function setRange(r: TimeRange) {
  range.value = r;
  loadCatalog();
  runQuery();
}

// --- Poll -----------------------------------------------------------------
let pollTimer: ReturnType<typeof setInterval> | undefined;
function startPoll() {
  stopPoll();
  pollTimer = setInterval(() => {
    if (!live.value) return;
    // loadCatalog self-guards against overlap; skip a range refresh while one is
    // still in flight so a slow query can't stack requests on the scrape cadence.
    loadCatalog();
    if (builder.value && !queryLoading.value) runQuery();
  }, POLL_MS);
}
function stopPoll() {
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = undefined;
}

watch(live, (on) => {
  if (on) {
    loadCatalog();
    if (builder.value) runQuery();
  }
});

onMounted(() => {
  palette.value = resolveVizPalette(document.body);
  loadCatalog();
  startPoll();
});
onBeforeUnmount(stopPoll);
</script>

<style scoped>
.metrics {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
}
.toolbar {
  display: flex;
  align-items: center;
  gap: var(--lg-space-2);
  flex-wrap: wrap;
  padding: var(--lg-space-2) var(--lg-space-3);
  border-bottom: 1px solid var(--lg-border);
  flex-shrink: 0;
}
.toolbar-spacer {
  flex: 1;
  min-width: 0;
}
.live-toggle.live {
  color: var(--lg-ok);
}
.live-toggle.off {
  color: var(--lg-text-faint);
}

.body {
  display: flex;
  flex: 1;
  min-height: 0;
}
.catalog-panel {
  width: 340px;
  flex-shrink: 0;
  border-right: 1px solid var(--lg-border);
  min-height: 0;
  overflow: hidden;
}
.canvas {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-3);
  padding: var(--lg-space-3);
  overflow-y: auto;
}
.builder-bar {
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-2);
}
.chip-row,
.control-row,
.options-row {
  display: flex;
  align-items: center;
  gap: var(--lg-space-2);
  flex-wrap: wrap;
}
.options-row {
  padding: var(--lg-space-2);
  border: 1px solid var(--lg-border-faint);
  border-radius: var(--lg-radius-md);
  background: var(--lg-bg-surface);
}
.opt {
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-1);
}
.opt-label {
  font-size: var(--lg-fs-xs);
  color: var(--lg-text-faint);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.single-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: var(--lg-space-3);
}
.canvas-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--lg-space-2);
  padding: var(--lg-space-6);
  color: var(--lg-text-faint);
  text-align: center;
}
.canvas-empty.prompt {
  flex: 1;
}
.prompt-title {
  margin: 0;
  font-size: var(--lg-fs-lg);
  color: var(--lg-text-dim);
}
.prompt-sub {
  margin: 0;
  font-size: var(--lg-fs-sm);
  max-width: 42ch;
}
.truncate-note {
  margin: 0;
  font-size: var(--lg-fs-xs);
  color: var(--lg-warn);
}
.legend {
  max-height: 320px;
}

.only-mobile {
  display: none;
}
@media (max-width: 768px) {
  .only-desktop {
    display: none !important;
  }
  .only-mobile {
    display: inline-flex;
  }
  .canvas {
    padding: var(--lg-space-2);
  }
}
</style>
